import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ClipboardDocumentListIcon,
    BoltIcon,
    EnvelopeOpenIcon,
    SparklesIcon,
    ChartBarIcon,
    UserGroupIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    StarIcon,
    LockClosedIcon,
    XMarkIcon,
    PlayCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { IconLanguageSelector } from '../ui/LanguageSelector';
import CookieConsent from './CookieConsent';

function useMeta({ title, description, canonical }) {
    React.useEffect(() => {
        const setMeta = (name, content) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
            el.setAttribute('content', content);
        };

        const setOG = (property, content) => {
            let el = document.querySelector(`meta[property="${property}"]`);
            if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
            el.setAttribute('content', content);
        };

        if (title) document.title = title;
        if (description) setMeta('description', description);

        if (canonical) {
            let link = document.querySelector('link[rel="canonical"]');
            if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
            link.href = canonical;
        }

        // Preload hero logo (LCP)
        if (!document.querySelector('link[data-preload-logo]')) {
            const l = document.createElement('link');
            l.rel = 'preload';
            l.as = 'image';
            l.href = '/LiveLoveSpain.png';
            l.setAttribute('data-preload-logo', 'true');
            document.head.appendChild(l);
        }

        // OG + Twitter - use canonical URL for og:url
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
        const baseUrl = isSubdomain ? 'https://crm.livelovespain.com' : 'https://www.livelovespain.com/crm';
        const ogUrl = canonical || (isSubdomain ? 'https://crm.livelovespain.com/' : 'https://www.livelovespain.com/crm/');
        
        setOG('og:type', 'website');
        setOG('og:title', title || 'LiveLoveSpain CRM');
        setOG('og:description', description || 'CRM for relocation agencies');
        setOG('og:url', ogUrl);
        setOG('og:image', 'https://www.livelovespain.com/og/crm-hero.png');
        setMeta('twitter:card', 'summary_large_image');

        // JSON-LD Schemas
        
        // Organization Schema
        const orgId = 'jsonld-organization';
        let orgScript = document.getElementById(orgId);
        const orgPayload = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "LiveLoveSpain",
            "url": "https://www.livelovespain.com",
            "logo": "https://www.livelovespain.com/LiveLoveSpain-512.png",
            "contactPoint": {
                "@type": "ContactPoint",
                "email": "info@livelovespain.com",
                "contactType": "Customer Service"
            },
            "sameAs": [
                "https://www.livelovespain.com"
            ]
        };
        if (!orgScript) { 
            orgScript = document.createElement('script'); 
            orgScript.type = 'application/ld+json'; 
            orgScript.id = orgId; 
            document.head.appendChild(orgScript); 
        }
        orgScript.text = JSON.stringify(orgPayload);

        // Website Schema
        const websiteId = 'jsonld-website';
        let websiteScript = document.getElementById(websiteId);
        const websitePayload = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "LiveLoveSpain CRM",
            "url": baseUrl,
            "publisher": {
                "@type": "Organization",
                "name": "LiveLoveSpain"
            },
            "potentialAction": {
                "@type": "SearchAction",
                "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": baseUrl + "/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
            }
        };
        if (!websiteScript) { 
            websiteScript = document.createElement('script'); 
            websiteScript.type = 'application/ld+json'; 
            websiteScript.id = websiteId; 
            document.head.appendChild(websiteScript); 
        }
        websiteScript.text = JSON.stringify(websitePayload);

        // Enhanced SoftwareApplication Schema
        const softwareId = 'jsonld-software';
        let softwareScript = document.getElementById(softwareId);
        const softwarePayload = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "LiveLoveSpain CRM",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
                "@type": "Offer",
                "price": "39.00",
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock"
            },
            "description": "Run your entire relocation business from one platform. Multi-tenant CRM with workflows, AI Copilot, client portals, and white-label capabilities.",
            "url": baseUrl,
            "author": {
                "@type": "Organization",
                "name": "LiveLoveSpain"
            },
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "150"
            },
            "featureList": [
                "Multi-tenant CRM",
                "Workflow Automation",
                "AI Copilot",
                "Client Portals",
                "White-label Capabilities",
                "Task Management",
                "Email Campaigns",
                "Form Builder"
            ]
        };
        if (!softwareScript) { 
            softwareScript = document.createElement('script'); 
            softwareScript.type = 'application/ld+json'; 
            softwareScript.id = softwareId; 
            document.head.appendChild(softwareScript); 
        }
        softwareScript.text = JSON.stringify(softwarePayload);
    }, [title, description, canonical]);
}

const LandingPage = () => {
    const { t } = useTranslation();
    
    // Get canonical URL based on hostname
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
    const canonicalUrl = isSubdomain 
        ? 'https://crm.livelovespain.com/'
        : 'https://www.livelovespain.com/crm/';

    useMeta({
        title: 'LiveLoveSpain CRM – Purpose-built for relocation & mobility agencies',
        description: 'Run your entire relocation business from one platform. Multi-tenant CRM with workflows, AI Copilot, client portals, and white-label capabilities.',
        canonical: canonicalUrl
    });

    const [showStickyCTA, setShowStickyCTA] = useState(false);
    const [showTopBanner, setShowTopBanner] = useState(true);
    const [rotatingTextIndex, setRotatingTextIndex] = useState(0);
    const [customerLogoIndex, setCustomerLogoIndex] = useState(0);
    const [billingPeriod, setBillingPeriod] = useState('annual'); // 'monthly' or 'annual'
    const [featureCarouselIndex, setFeatureCarouselIndex] = useState(0);
    const [pauseCarousel, setPauseCarousel] = useState(false);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const carouselViewportRef = React.useRef(null);
    const [slideWidthPx, setSlideWidthPx] = useState(0);
    const SLIDE_GAP_PX = 16; // gap between slides
    const demoRef = React.useRef(null);
    const [demoVisible, setDemoVisible] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [headerShadow, setHeaderShadow] = useState(false);
    const headerRef = React.useRef(null);

    // Measure slide width synchronously before paint to avoid first-frame jitter
    const [viewportWidth, setViewportWidth] = React.useState(0);
    React.useLayoutEffect(() => {
        const measure = () => {
            const vp = carouselViewportRef.current;
            const w = vp?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
            if (!w) return;
            const perSlide = Math.max(220, Math.floor((w - (2 * SLIDE_GAP_PX)) / 3));
            setSlideWidthPx(perSlide);
            setViewportWidth(w);
        };
        measure();
        window.addEventListener('resize', measure, { passive: true });
        return () => window.removeEventListener('resize', measure);
    }, []);

    const rotatingWords = [
        t('landing.hero.rotatingWords.grow'),
        t('landing.hero.rotatingWords.scale'),
        t('landing.hero.rotatingWords.automate'),
        t('landing.hero.rotatingWords.succeed')
    ];
    const customerLogos = [
        { name: 'Agency 1', placeholder: true },
        { name: 'Agency 2', placeholder: true },
        { name: 'Agency 3', placeholder: true },
        { name: 'Agency 4', placeholder: true }
    ];

    useEffect(() => {
        const onScroll = () => {
            setShowStickyCTA(window.scrollY > 400);
            setHeaderShadow(window.scrollY > 2);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Handle ESC key to close enlarged image
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && enlargedImage) {
                setEnlargedImage(null);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [enlargedImage]);

    // Animate rotating text
    useEffect(() => {
        const interval = setInterval(() => {
            setRotatingTextIndex((prev) => (prev + 1) % rotatingWords.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    // Auto-rotate customer logos
    useEffect(() => {
        const interval = setInterval(() => {
            setCustomerLogoIndex((prev) => (prev + 1) % customerLogos.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    // Feature carousel data - defined early to avoid temporal dead zone
    const featureCarousel = [
        {
            id: 'ai-email-templates',
            title: t('landing.features.carousel.aiEmailTemplates.title') || 'AI-Enabled Email Templates',
            description: t('landing.features.carousel.aiEmailTemplates.description') || 'Generate professional email templates instantly with AI. Create personalized campaigns with smart variable replacement and default values.',
            image: '/features/email-template.png',
            badge: 'Enterprise',
            link: '#features'
        },
        {
            id: 'email-campaigns',
            title: t('landing.features.carousel.emailCampaigns.title') || 'Email Campaigns',
            description: t('landing.features.carousel.emailCampaigns.description') || 'Launch targeted email campaigns with broadcast and sequence options. Track opens, clicks, and engagement with detailed analytics.',
            image: '/features/campaigns.png', 
            badge: null,
            link: '#features'
        },
        {
            id: 'ai-copilot',
            title: t('landing.features.carousel.aiCopilot.title'),
            description: t('landing.features.carousel.aiCopilot.description'),
            image: '/features/ai-copilot.png',
            badge: null,
            link: '#features'
        },
        {
            id: 'client-portal',
            title: t('landing.features.carousel.clientPortal.title'),
            description: t('landing.features.carousel.clientPortal.description'),
            image: '/features/client-portal.png',
            badge: null,
            link: '#features'
        },
        {
            id: 'workflow-automation',
            title: t('landing.features.carousel.workflowAutomation.title'),
            description: t('landing.features.carousel.workflowAutomation.description'),
            image: '/features/workflows.png',
            badge: 'Pro',
            link: '#features'
        },
        {
            id: 'analytics',
            title: t('landing.features.carousel.analytics.title'),
            description: t('landing.features.carousel.analytics.description'),
            image: '/features/analytics.png',
            badge: null,
            link: '#features'
        },
        {
            id: 'form-builder',
            title: t('landing.features.carousel.formBuilder.title'),
            description: t('landing.features.carousel.formBuilder.description'),
            image: '/features/forms.png',
            badge: 'Pro',
            link: '#features'
        },
        {
            id: 'white-label',
            title: t('landing.features.carousel.whiteLabel.title'),
            description: t('landing.features.carousel.whiteLabel.description'),
            image: '/features/whitelabel.png',
            badge: 'Pro',
            link: '#features'
        },
        {
            id: 'team-collaboration',
            title: t('landing.features.carousel.teamCollaboration.title'),
            description: t('landing.features.carousel.teamCollaboration.description'),
            image: '/features/collaboration.png',
            badge: 'Enterprise',
            link: '#features'
        }
    ];

    // Auto-rotate feature carousel (pauses on hover)
    useEffect(() => {
        if (pauseCarousel) return;
        const id = setInterval(() => {
            setFeatureCarouselIndex((p) => (p + 1) % featureCarousel.length);
        }, 5000);
        return () => clearInterval(id);
    }, [pauseCarousel, featureCarousel.length]);

    // Lazy-load demo video when visible
    useEffect(() => {
        const el = demoRef.current;
        if (!el) return;
        const io = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setDemoVisible(true);
        }, { rootMargin: '200px' });
        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Monthly prices (base prices)
    const monthlyPrices = {
        starter: 39,
        professional: 86,
        enterprise: 199
    };

    // Calculate annual prices (10x monthly) and discount percentage
    const annualDiscount = Math.round((1 - 10/12) * 100); // ~17% discount

    const getPrice = (planId) => {
        if (planId === 'trial') {
            return { price: 'Free', period: '14 days' };
        }
        
        const monthlyPrice = monthlyPrices[planId];
        if (!monthlyPrice) {
            // Enterprise plan
            if (billingPeriod === 'annual') {
                return { price: '€1,990+', period: '/year' };
            } else {
                return { price: '€199+', period: '/month' };
            }
        }
        
        if (billingPeriod === 'annual') {
            const annualPrice = monthlyPrice * 10;
            return { price: `€${annualPrice}`, period: '/year' };
        } else {
            return { price: `€${monthlyPrice}`, period: '/month' };
        }
    };

    const plans = [
        {
            id: 'trial',
            name: 'Trial',
            monthlyPrice: null,
            seats: 'Up to 3',
            agencies: '1',
            clients: '50',
            popular: false,
            features: [
                'Document Management',
                'Basic Analytics',
                'Basic Forms',
                'Task Templates',
                'Email Templates'
            ],
            cta: 'Start Free Trial',
            ctaLink: '/signup'
        },
        {
            id: 'starter',
            name: 'Starter',
            monthlyPrice: 39,
            seats: 'Up to 3',
            agencies: '1',
            clients: '100',
            popular: false,
            features: [
                'Client Portal',
                'Location Maps',
                'Custom Branding',
                'All Trial features',
                'Priority Email Support'
            ],
            cta: 'Start Free Trial',
            ctaLink: '/signup'
        },
        {
            id: 'professional',
            name: 'Professional',
            monthlyPrice: 86,
            seats: 'Up to 10',
            agencies: '1',
            clients: '500',
            popular: true,
            features: [
                'AI Copilot',
                'Advanced Analytics',
                'Email Campaigns',
                'Account Portal',
                'API Access',
                'White Label',
                'Multi-page Forms',
                'Conditional Logic',
                'Workflow Automation',
                'Priority Support'
            ],
            cta: 'Start Free Trial',
            ctaLink: '/signup'
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            monthlyPrice: 199,
            seats: 'Inquire',
            agencies: 'Inquire',
            clients: 'Inquire',
            popular: false,
            features: [
                'All Professional features',
                'Collaboration Hub',
                'Webhooks',
                'Dedicated Manager',
                'Audit Logs',
                'Custom Domain',
                'White-label Mode'
            ],
            cta: 'Contact Sales',
            ctaLink: '/contact'
        }
    ];

    const valueProps = [
        {
            icon: UserGroupIcon,
            title: t('landing.valueProps.multiTenancy.title'),
            description: t('landing.valueProps.multiTenancy.description')
        },
        {
            icon: BoltIcon,
            title: t('landing.valueProps.automation.title'),
            description: t('landing.valueProps.automation.description')
        },
        {
            icon: ClipboardDocumentListIcon,
            title: t('landing.valueProps.formBuilder.title'),
            description: t('landing.valueProps.formBuilder.description')
        },
        {
            icon: GlobeAltIcon,
            title: t('landing.valueProps.localization.title'),
            description: t('landing.valueProps.localization.description')
        },
        {
            icon: SparklesIcon,
            title: t('landing.valueProps.aiInsights.title'),
            description: t('landing.valueProps.aiInsights.description')
        },
        {
            icon: ShieldCheckIcon,
            title: t('landing.valueProps.whiteLabel.title'),
            description: t('landing.valueProps.whiteLabel.description')
        }
    ];

    const segments = [
        {
            name: t('landing.segmentation.solo.name'),
            profile: t('landing.segmentation.solo.profile'),
            plan: t('landing.segmentation.solo.plan'),
            message: t('landing.segmentation.solo.message')
        },
        {
            name: t('landing.segmentation.growing.name'),
            profile: t('landing.segmentation.growing.profile'),
            plan: t('landing.segmentation.growing.plan'),
            message: t('landing.segmentation.growing.message')
        },
        {
            name: t('landing.segmentation.network.name'),
            profile: t('landing.segmentation.network.profile'),
            plan: t('landing.segmentation.network.plan'),
            message: t('landing.segmentation.network.message')
        }
    ];

    const integrations = [
        'Stripe', 'Zapier', 'Slack', 'Google', 'WhatsApp', 'Facebook',
        'LinkedIn', 'Shopify', 'Mailchimp', 'Gmail', 'Outlook', 'Zoom'
    ];

    // Highlighted plan column (hover/tap)
    const [hoveredPlanIdx, setHoveredPlanIdx] = useState(null);

    // Feature comparison data (sync with backend/config/subscriptionPlans.js)
    const plansOrder = ['Starter', 'Professional', 'Enterprise'];
    const planIds = ['starter', 'professional', 'enterprise'];
    const planNameById = {
        starter: 'Starter',
        professional: 'Professional',
        enterprise: 'Enterprise'
    };
    const featureCategories = [
        {
            name: t('landing.comparison.categories.coreLimits'),
            rows: [
                { label: t('landing.comparison.features.maxSeats'), values: ['3', '10', t('landing.comparison.inquire')] },
                { label: t('landing.comparison.features.maxAgencies'), values: ['1', '1', t('landing.comparison.inquire')] },
                { label: t('landing.comparison.features.maxClients'), values: ['100', '500', t('landing.comparison.inquire')] },
                { label: t('landing.comparison.features.maxAccounts'), values: ['50', '200', t('landing.comparison.inquire')] }
            ]
        },
        {
            name: t('landing.comparison.categories.aiAutomation'),
            rows: [
                { label: t('landing.comparison.features.aiCopilot'), values: [false, true, true] },
                { label: t('landing.comparison.features.workflowAutomation'), values: [false, true, true] }
            ]
        },
        {
            name: t('landing.comparison.categories.portalsCollaboration'),
            rows: [
                { label: t('landing.comparison.features.clientPortal'), values: [true, true, true] },
                { label: t('landing.comparison.features.accountPortal'), values: [false, true, true] },
                { label: t('landing.comparison.features.collaborationHub'), values: [false, true, true] }
            ]
        },
        {
            name: t('landing.comparison.categories.analytics'),
            rows: [
                { label: t('landing.comparison.features.basicAnalytics'), values: [true, true, true] },
                { label: t('landing.comparison.features.advancedAnalytics'), values: [false, true, true] }
            ]
        },
        {
            name: t('landing.comparison.categories.brandingApi'),
            rows: [
                { label: t('landing.comparison.features.customBranding'), values: [true, true, true] },
                { label: t('landing.comparison.features.whiteLabel'), values: [false, true, true] },
                { label: t('landing.comparison.features.customDomain'), values: [false, true, true] },
                { label: t('landing.comparison.features.apiAccess'), values: [false, true, true] },
                { label: t('landing.comparison.features.webhooks'), values: [false, false, true] }
            ]
        },
        {
            name: t('landing.comparison.categories.support'),
            rows: [
                { label: t('landing.comparison.features.standardSupport'), values: [true, true, true] },
                { label: t('landing.comparison.features.prioritySupport'), values: [false, true, true] },
                { label: t('landing.comparison.features.dedicatedManager'), values: [false, false, true] }
            ]
        }
    ];

    const renderCell = (val) => {
        if (typeof val === 'boolean') {
            return (
                <div className="flex items-center justify-center">
                    {val ? (
                        <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                        <XMarkIcon className="h-5 w-5 text-rose-500" aria-hidden="true" />
                    )}
                </div>
            );
        }
        return <span className="text-foreground text-sm font-medium">{val}</span>;
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Enhanced Top Nav Header */}
            <header 
                ref={headerRef}
                className={`sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border transition-shadow ${
                    headerShadow ? 'shadow-sm' : ''
                }`}
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
                    {/* Logo */}
                    <a href="/" className="flex items-center gap-2">
                        <img 
                            src="/LiveLoveSpain.png" 
                            alt="LiveLoveSpain" 
                            width={120} 
                            height={24} 
                            className="h-6 w-auto" 
                        />
                        <span className="sr-only">LiveLoveSpain</span>
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8 text-sm">
                        <a 
                            href="#features" 
                            className="text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded transition-colors"
                        >
                            {t('landing.nav.features')}
                        </a>
                        <a 
                            href="#compare" 
                            className="text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded transition-colors"
                        >
                            {t('landing.nav.pricing')}
                        </a>
                        <a 
                            href="#faq" 
                            className="text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded transition-colors"
                        >
                            {t('landing.nav.faq')}
                        </a>
                        <a 
                            href="/contact" 
                            className="text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded transition-colors"
                        >
                            {t('landing.nav.contact')}
                        </a>
                    </nav>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-2">
                        <IconLanguageSelector className="h-9" />
                        <a 
                            href="/login"
                            className="text-sm inline-flex items-center h-9 px-3 rounded-md border border-border text-foreground/80 hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 transition-colors"
                        >
                            {t('landing.nav.login')}
                        </a>
                        <a 
                            href="/signup"
                            className="text-sm inline-flex items-center h-9 px-3 rounded-md bg-rose-500 hover:bg-rose-600 text-white font-semibold shadow-sm hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 transition-all"
                        >
                            {t('landing.hero.startTrial')}
                        </a>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-controls="mobile-menu"
                        aria-expanded={mobileMenuOpen}
                        aria-label={mobileMenuOpen ? t('landing.nav.closeMenu', 'Close menu') : t('landing.nav.openMenu', 'Open menu')}
                        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-border text-foreground/80 hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 transition-colors"
                    >
                        {mobileMenuOpen ? (
                            <XMarkIcon className="h-5 w-5" />
                        ) : (
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-5 w-5" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div 
                        id="mobile-menu"
                        className="md:hidden border-t border-border bg-background/95 backdrop-blur-sm"
                    >
                        <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-1">
                            <a 
                                href="#features" 
                                className="py-2 text-sm rounded hover:bg-muted px-2 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('landing.nav.features')}
                            </a>
                            <a 
                                href="#compare" 
                                className="py-2 text-sm rounded hover:bg-muted px-2 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('landing.nav.pricing')}
                            </a>
                            <a 
                                href="#faq" 
                                className="py-2 text-sm rounded hover:bg-muted px-2 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('landing.nav.faq')}
                            </a>
                            <a 
                                href="/contact" 
                                className="py-2 text-sm rounded hover:bg-muted px-2 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('landing.nav.contact')}
                            </a>
                            <div className="pt-2 flex items-center gap-2">
                                <a 
                                    href="/login"
                                    className="flex-1 inline-flex items-center justify-center h-10 rounded-md border border-border text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('landing.nav.login')}
                                </a>
                                <a 
                                    href="/signup"
                                    className="flex-1 inline-flex items-center justify-center h-10 rounded-md bg-rose-500 hover:bg-rose-600 text-white font-semibold transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('landing.hero.startTrial')}
                                </a>
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
                <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
                    <div className="max-w-4xl mx-auto text-center">
                        {/* Main Headline */}
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                            {t('landing.hero.title')}
                        </h1>

                        <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
                            {t('landing.hero.subtitle')}{' '}
                            <span className="inline-block whitespace-nowrap text-rose-400 transition-colors duration-300">
                                {rotatingWords[rotatingTextIndex]}
                            </span>
                            .
                        </p>

                        {/* Key Features Highlight - SEO: Using H3 for feature items (H1 → H3 hierarchy) */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto" role="list">
                            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                                <SparklesIcon className="h-8 w-8 mx-auto mb-2 text-rose-400" aria-hidden="true" />
                                <h3 className="text-sm font-semibold text-white mb-1">{t('landing.hero.features.aiCopilot')}</h3>
                                <p className="text-xs text-blue-200 mt-1">{t('landing.hero.features.aiCopilotDesc')}</p>
                            </div>
                            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                                <GlobeAltIcon className="h-8 w-8 mx-auto mb-2 text-rose-400" aria-hidden="true" />
                                <h3 className="text-sm font-semibold text-white mb-1">{t('landing.hero.features.clientPortal')}</h3>
                                <p className="text-xs text-blue-200 mt-1">{t('landing.hero.features.clientPortalDesc')}</p>
                            </div>
                            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                                <BoltIcon className="h-8 w-8 mx-auto mb-2 text-rose-400" aria-hidden="true" />
                                <h3 className="text-sm font-semibold text-white mb-1">{t('landing.hero.features.workflows')}</h3>
                                <p className="text-xs text-blue-200 mt-1">{t('landing.hero.features.workflowsDesc')}</p>
                            </div>
                            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" role="listitem">
                                <ShieldCheckIcon className="h-8 w-8 mx-auto mb-2 text-rose-400" aria-hidden="true" />
                                <h3 className="text-sm font-semibold text-white mb-1">{t('landing.hero.features.whiteLabel')}</h3>
                                <p className="text-xs text-blue-200 mt-1">{t('landing.hero.features.whiteLabelDesc')}</p>
                            </div>
                        </div>

                        {/* Email Capture Form + CTA */}
                        <form
                            className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-3"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                const email = new FormData(e.currentTarget).get('email');
                                const emailValue = email?.trim() || '';
                                
                                // Record interest if email provided
                                if (emailValue) {
                                    try {
                                        const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
                                        // Fire and forget - don't block user flow
                                        fetch(`${apiUrl}/api/trial-interest`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify({
                                                email: emailValue,
                                                source: 'landing_page',
                                                referrer: document.referrer || window.location.href
                                            })
                                        }).catch(() => {}); // Silently fail
                                    } catch (error) {
                                        // Continue anyway - don't block user
                                    }
                                }
                                
                                // Navigate to signup with email if provided
                                const signupUrl = emailValue 
                                    ? `/signup?email=${encodeURIComponent(emailValue)}`
                                    : '/signup';
                                window.location.href = signupUrl;
                            }}
                        >
                            <label htmlFor="email" className="sr-only">{t('landing.hero.emailPlaceholder')}</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                placeholder={t('landing.hero.emailPlaceholder')}
                                className="w-full sm:w-80 h-12 rounded-lg border border-white/30 bg-white/95 text-gray-900 px-4 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-300"
                            />
                            <button
                                type="submit"
                                className="w-full sm:w-auto inline-flex items-center justify-center h-12 px-6 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                {t('landing.hero.startTrial')} <ArrowRightIcon className="ml-2 h-5 w-5" />
                            </button>
                            <a
                                href="#demo"
                                className="w-full sm:w-auto inline-flex items-center justify-center h-12 px-6 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/20"
                            >
                                <PlayCircleIcon className="mr-2 h-5 w-5" /> {t('landing.hero.watchDemo')}
                            </a>
                        </form>
                        <p className="text-sm text-blue-200">{t('landing.hero.trialNote')}</p>
                    </div>
                </div>
            </section>

            {/* Proof Metrics (replaces logos & awards) - Hidden for now */}
            {false && (
                <section className="py-8 bg-muted border-y border-border">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { n: '2,100+', l: t('landing.proofMetrics.clientFiles') },
                                { n: '30–50%', l: t('landing.proofMetrics.lessAdmin') },
                                { n: 'NPS 62', l: t('landing.proofMetrics.customerSatisfaction') },
                                { n: t('landing.proofMetrics.euHosted'), l: t('landing.proofMetrics.gdprCompliant') },
                            ].map((i, idx) => (
                                <div key={idx} className="text-center rounded-lg bg-card border border-border p-4">
                                    <div className="text-xl font-bold text-foreground">{i.n}</div>
                                    <div className="text-xs text-muted-foreground">{i.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Value Propositions - Bento Box Layout */}
            <section className="py-16 sm:py-20 lg:py-24 bg-card">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                            {t('landing.valueProps.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('landing.valueProps.subtitle')}
                        </p>
                    </div>

                    {/* Bento Box Grid - Varied card sizes for visual interest */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {/* Large featured card - spans 2 columns, taller */}
                        <div className="md:col-span-2 lg:col-span-2 lg:row-span-2 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col justify-between min-h-[400px]">
                            <div>
                                <div className="w-16 h-16 rounded-xl bg-blue-600 dark:bg-blue-500 flex items-center justify-center mb-6 shadow-lg">
                                    {React.createElement(valueProps[0].icon, { className: "h-8 w-8 text-white" })}
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
                                    {valueProps[0].title}
                                </h3>
                                <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                                    {valueProps[0].description}
                                </p>
                            </div>
                            <div className="mt-6 pt-6 border-t border-blue-200 dark:border-blue-800">
                                <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                    Start managing multiple agencies from one platform
                                </p>
                            </div>
                        </div>

                        {/* Medium card - spans 2 columns horizontally */}
                        <div className="md:col-span-2 p-6 lg:p-8 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                                {React.createElement(valueProps[1].icon, { className: "h-6 w-6 text-green-600 dark:text-green-400" })}
                                    </div>
                                    <h3 className="text-xl font-semibold text-foreground mb-2">
                                {valueProps[1].title}
                                    </h3>
                                    <p className="text-muted-foreground">
                                {valueProps[1].description}
                                    </p>
                                </div>

                        {/* Small card */}
                        <div className="p-6 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
                                {React.createElement(valueProps[2].icon, { className: "h-6 w-6 text-purple-600 dark:text-purple-400" })}
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                {valueProps[2].title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {valueProps[2].description}
                            </p>
                        </div>

                        {/* Small card */}
                        <div className="p-6 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-4">
                                {React.createElement(valueProps[3].icon, { className: "h-6 w-6 text-orange-600 dark:text-orange-400" })}
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                {valueProps[3].title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {valueProps[3].description}
                            </p>
                        </div>

                        {/* Medium card - spans 2 columns horizontally */}
                        <div className="md:col-span-2 p-6 lg:p-8 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center mb-4">
                                {React.createElement(valueProps[4].icon, { className: "h-6 w-6 text-white" })}
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-2">
                                {valueProps[4].title}
                            </h3>
                            <p className="text-muted-foreground">
                                {valueProps[4].description}
                            </p>
                        </div>

                        {/* Small card - White Label */}
                        <div className="p-6 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-4">
                                {React.createElement(valueProps[5].icon, { className: "h-6 w-6 text-rose-600 dark:text-rose-400" })}
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                {valueProps[5].title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {valueProps[5].description}
                            </p>
                        </div>

                        {/* Small card - Email Campaigns */}
                        <div className="p-6 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center mb-4">
                                <EnvelopeOpenIcon className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                {t('landing.keyFeatures.emailCampaigns.title')}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {t('landing.keyFeatures.emailCampaigns.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Demo Section (inspired by HighLevel) - Hidden for now */}
            {/* <section id="demo" className="py-16 sm:py-20 lg:py-24 bg-muted">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                                {t('landing.demo.title')}
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                {t('landing.demo.subtitle')}
                            </p>
                        </div>
                        <div ref={demoRef} className="relative aspect-video bg-card rounded-xl border border-border overflow-hidden shadow-xl">
                            {demoVisible ? (
                                <video
                                    src="/videos/crm-demo-loop.mp4"
                                    controls
                                    playsInline
                                    poster="/videos/crm-demo-poster.jpg"
                                    className="w-full h-full object-cover"
                                    preload="metadata"
                                />
                            ) : (
                                <img
                                    src="/videos/crm-demo-poster.jpg"
                                    alt={t('landing.demo.alt')}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section> */}

            {/* Pricing Section removed */}

            {/* Segmentation Section */}
            <section className="py-16 sm:py-20 lg:py-24 bg-muted">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                            {t('landing.segmentation.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('landing.segmentation.subtitle')}
                        </p>
                        </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
                        {segments.map((segment, index) => (
                            <div
                                key={index}
                                className="p-6 rounded-xl bg-card border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg"
                            >
                                <div className="mb-4">
                                    <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full">
                                        {segment.plan}
                                    </span>
                        </div>
                                <h3 className="text-xl font-semibold text-foreground mb-2">
                                    {segment.name}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {segment.profile}
                                </p>
                                <p className="text-foreground font-medium">
                                    "{segment.message}"
                                </p>
                        </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Feature Carousel Section */}
            <section id="features" className="py-16 sm:py-20 lg:py-24 bg-card">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                            {t('landing.features.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('landing.features.subtitle')}
                        </p>
                    </div>

                    <div className="relative max-w-6xl mx-auto">
                        {/* Carousel Container */}
                        <div className="relative overflow-hidden rounded-xl py-12" style={{ minHeight: '420px' }}>
                            {/* Navigation Controls - Overlaid */}
                            <div className="absolute inset-0 flex items-center justify-between z-20 pointer-events-none px-4" style={{ top: '3rem', bottom: '3rem' }}>
                                <button
                                    onClick={() => setFeatureCarouselIndex((prev) => 
                                        prev === 0 ? featureCarousel.length - 1 : prev - 1
                                    )}
                                    className="pointer-events-auto p-3 rounded-full border border-border bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg transition-all hover:scale-110"
                                    aria-label={t('landing.features.previous')}
                                >
                                    <ChevronLeftIcon className="h-6 w-6 text-foreground" />
                                </button>
                                <button
                                    onClick={() => setFeatureCarouselIndex((prev) => 
                                        prev === featureCarousel.length - 1 ? 0 : prev + 1
                                    )}
                                    className="pointer-events-auto p-3 rounded-full border border-border bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg transition-all hover:scale-110"
                                    aria-label={t('landing.features.next')}
                                >
                                    <ChevronRightIcon className="h-6 w-6 text-foreground" />
                                </button>
                            </div>

                            {/* Carousel Rail */}
                            <div
                                ref={carouselViewportRef}
                                className="relative overflow-hidden px-4 sm:px-6 lg:px-8"
                                onMouseEnter={() => setPauseCarousel(true)}
                                onMouseLeave={() => setPauseCarousel(false)}
                                onFocusCapture={() => setPauseCarousel(true)}
                                onBlurCapture={() => setPauseCarousel(false)}
                                role="region"
                                aria-roledescription="carousel"
                                aria-label="Feature highlights"
                            >
                                <div 
                                    className="flex transition-transform duration-500 ease-in-out will-change-transform"
                                    style={{ 
                                        columnGap: `${SLIDE_GAP_PX}px`,
                                        transform: slideWidthPx && viewportWidth
                                            ? (() => {
                                                // Calculate position for active card (accounting for clone at start)
                                                const activeIndex = featureCarouselIndex + 1; // +1 because first is clone
                                                const cardPosition = (slideWidthPx + SLIDE_GAP_PX) * activeIndex;
                                                // Center the active card: viewport center - card center
                                                const viewportCenter = viewportWidth / 2;
                                                const cardCenter = cardPosition + slideWidthPx / 2;
                                                const offset = viewportCenter - cardCenter;
                                                return `translate3d(${Math.round(offset)}px, 0, 0)`;
                                            })()
                                            : slideWidthPx
                                            ? `translate3d(-${Math.round((slideWidthPx + SLIDE_GAP_PX) * (featureCarouselIndex))}px, 0, 0)`
                                            : `translate3d(-${featureCarouselIndex * (100 / 3)}%, 0, 0)`
                                    }}
                                >
                                    {/* Create clones for infinite effect - show previous, current, next */}
                                    {[
                                        ...featureCarousel.slice(-1), // Previous (clone)
                                        ...featureCarousel,           // Current set
                                        ...featureCarousel.slice(0, 1) // Next (clone)
                                    ].map((feature, index) => {
                                        const activeIndex = featureCarouselIndex + 1; // +1 because first is previous clone
                                        const distance = Math.abs(index - activeIndex);
                                        const isActive = index === activeIndex;
                                        const isAdjacent = distance === 1;
                                        
                                        // Get the actual feature index for the clone
                                        const actualIndex = index === 0 
                                            ? featureCarousel.length - 1  // Previous
                                            : index === featureCarousel.length + 1
                                            ? 0  // Next
                                            : index - 1;  // Current set
                                        
                                        return (
                                            <div 
                                                key={`${feature.id}-${index}`}
                                                className="flex-shrink-0 overflow-visible"
                                                style={{ width: slideWidthPx ? `${slideWidthPx}px` : '33.3333%' }}
                                                role="group"
                                                aria-label={`${actualIndex + 1} of ${featureCarousel.length}`}
                                                aria-hidden={!isActive && !isAdjacent}
                                            >
                                                <div 
                                                    className={cn(
                                                    "bg-card rounded-xl border border-border p-4 sm:p-5 shadow-md origin-center transition-transform duration-500 will-change-transform transform-gpu",
                                                    isActive 
                                                        ? "scale-105 opacity-100 blur-0 z-10" 
                                                            : isAdjacent
                                                            ? "scale-95 opacity-85 blur-[0.5px] z-0 cursor-pointer"
                                                        : "scale-95 opacity-85 blur-[0.5px] pointer-events-none z-0"
                                                    )}
                                                    onClick={() => {
                                                        if (isAdjacent && !isActive) {
                                                            // Determine direction: left (previous) or right (next)
                                                            if (index < activeIndex) {
                                                                // Left card clicked - move to previous
                                                                setFeatureCarouselIndex((prev) => 
                                                                    prev === 0 ? featureCarousel.length - 1 : prev - 1
                                                                );
                                                            } else {
                                                                // Right card clicked - move to next
                                                                setFeatureCarouselIndex((prev) => 
                                                                    prev === featureCarousel.length - 1 ? 0 : prev + 1
                                                                );
                                                            }
                                                        }
                                                    }}
                                                    role={isAdjacent ? "button" : undefined}
                                                    tabIndex={isAdjacent ? 0 : undefined}
                                                    onKeyDown={(e) => {
                                                        if (isAdjacent && !isActive && (e.key === 'Enter' || e.key === ' ')) {
                                                            e.preventDefault();
                                                            if (index < activeIndex) {
                                                                setFeatureCarouselIndex((prev) => 
                                                                    prev === 0 ? featureCarousel.length - 1 : prev - 1
                                                                );
                                                            } else {
                                                                setFeatureCarouselIndex((prev) => 
                                                                    prev === featureCarousel.length - 1 ? 0 : prev + 1
                                                                );
                                                            }
                                                        }
                                                    }}
                                                    aria-label={isAdjacent ? `Navigate to ${feature.title}` : undefined}
                                                >
                                                    <div className="flex flex-col items-center text-center gap-4">
                                                        {/* Image */}
                                                        <div className="w-[220px] h-[220px] sm:w-[240px] sm:h-[240px]">
                                                            <div 
                                                                className={cn(
                                                                    "aspect-square rounded-lg bg-muted overflow-hidden border border-border transition-opacity",
                                                                    isActive 
                                                                        ? "cursor-pointer hover:opacity-90" 
                                                                        : "cursor-default pointer-events-none"
                                                                )}
                                                                onClick={isActive ? (e) => {
                                                                    e.stopPropagation(); // Prevent card navigation when clicking image
                                                                    setEnlargedImage({ src: feature.image, title: feature.title });
                                                                } : undefined}
                                                                role={isActive ? "button" : undefined}
                                                                tabIndex={isActive ? 0 : undefined}
                                                                onKeyDown={isActive ? (e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setEnlargedImage({ src: feature.image, title: feature.title });
                                                                    }
                                                                } : undefined}
                                                                aria-label={isActive ? `View enlarged image of ${feature.title}` : undefined}
                                                            >
                                                                <img 
                                                                    src={feature.image} 
                                                                    alt={feature.title}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        // Create a data URI placeholder instead of external URL
                                                                        const canvas = document.createElement('canvas');
                                                                        canvas.width = 480;
                                                                        canvas.height = 480;
                                                                        const ctx = canvas.getContext('2d');
                                                                        if (ctx) {
                                                                            // Background
                                                                            ctx.fillStyle = '#3B82F6';
                                                                            ctx.fillRect(0, 0, 480, 480);
                                                                            // Text
                                                                            ctx.fillStyle = '#FFFFFF';
                                                                            ctx.font = 'bold 24px Arial';
                                                                            ctx.textAlign = 'center';
                                                                            ctx.textBaseline = 'middle';
                                                                            const text = feature.title.length > 30 ? feature.title.substring(0, 27) + '...' : feature.title;
                                                                            ctx.fillText(text, 240, 240);
                                                                            e.target.src = canvas.toDataURL();
                                                                        } else {
                                                                            // Fallback: use a simple colored div background
                                                                            e.target.style.display = 'none';
                                                                            const parent = e.target.parentElement;
                                                                            if (parent) {
                                                                                parent.style.backgroundColor = '#3B82F6';
                                                                                parent.style.display = 'flex';
                                                                                parent.style.alignItems = 'center';
                                                                                parent.style.justifyContent = 'center';
                                                                                parent.innerHTML = `<span style="color: white; font-weight: bold; text-align: center; padding: 20px;">${feature.title}</span>`;
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Content */}
                                                        <div className="w-full">
                                                            {feature.badge && (
                                                                <span className="inline-block mb-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-semibold rounded-full">
                                                                    {feature.badge}
                                                                </span>
                                                            )}
                                                            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">
                                                                {feature.title}
                                                            </h3>
                                                            <p className="text-xs sm:text-sm text-muted-foreground mb-3 max-w-[280px] mx-auto">
                                                                {feature.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                        </div>
                            </div>

                            {/* Dots Indicator */}
                            <div className="flex items-center justify-center gap-2 mt-6">
                                {featureCarousel.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setFeatureCarouselIndex(index)}
                                        className={`h-2 rounded-full transition-all ${
                                            index === featureCarouselIndex 
                                                ? 'w-8 bg-primary' 
                                                : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                                        }`}
                                        aria-label={`Go to feature ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Lightbox Modal */}
            {enlargedImage && (
                <div 
                    className="fixed inset-0 z-[9999] bg-black/90 dark:bg-black/95 flex items-center justify-center p-4"
                    onClick={() => setEnlargedImage(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged image view"
                >
                    <div 
                        className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setEnlargedImage(null)}
                            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                            aria-label="Close enlarged image"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                        
                        {/* Image */}
                        <img 
                            src={enlargedImage.src} 
                            alt={enlargedImage.title}
                            className="max-w-full max-h-[90vh] object-contain rounded-lg"
                        />
                        
                        {/* Title */}
                        {enlargedImage.title && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm font-medium">
                                {enlargedImage.title}
                    </div>
                        )}
                </div>
                </div>
            )}

            {/* Pricing Teaser */}
            <section className="py-6 bg-card border-y border-border">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-sm sm:text-base text-foreground">
                        {t('landing.pricing.teaser')}
                    </p>
                    <span className="text-xs text-muted-foreground block mt-1">{t('landing.pricing.migration')}</span>
                </div>
            </section>

            {/* Feature Comparison Table */}
            <section id="compare" className="py-16 sm:py-20 lg:py-24 bg-muted">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('landing.pricing.compareTitle')}</h2>
                        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">{t('landing.pricing.compareSubtitle')}</p>
                        {/* Billing period toggle (re-using main state) */}
                        <div className="mt-6 flex items-center justify-center">
                            <div className="relative inline-flex items-center">
                                <div className="inline-flex items-center rounded-lg border border-border bg-card p-1 shadow-sm">
                                    <Button
                                        type="button"
                                        variant={billingPeriod === 'annual' ? 'default' : 'ghost'}
                                        size="sm"
                                        onClick={() => setBillingPeriod('annual')}
                                        className={cn(
                                            'rounded-md px-4 py-2 text-sm font-medium transition-all',
                                            billingPeriod === 'annual'
                                                ? 'bg-primary !text-white shadow-sm cursor-default hover:!bg-primary hover:!text-white'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        )}
                                    >
                                        {t('landing.pricing.annual')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={billingPeriod === 'monthly' ? 'default' : 'ghost'}
                                        size="sm"
                                        onClick={() => setBillingPeriod('monthly')}
                                        className={cn(
                                            'rounded-md px-4 py-2 text-sm font-medium transition-all',
                                            billingPeriod === 'monthly'
                                                ? 'bg-primary !text-white shadow-sm cursor-default hover:!bg-primary hover:!text-white'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        )}
                                    >
                                        {t('landing.pricing.monthly')}
                                    </Button>
                </div>
                                <span
                                    className={cn(
                                        'absolute top-1/2 -translate-y-1/2 left-full ml-3 px-2.5 py-1 text-xs font-semibold rounded-full transition-opacity whitespace-nowrap inline-flex items-center',
                                        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
                                        billingPeriod === 'annual' ? 'opacity-100 visible' : 'opacity-0 invisible'
                                    )}
                                >
                                    {t('landing.pricing.save')} {annualDiscount}%
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-border bg-card">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-border bg-card sticky top-0 z-30">
                                    <th className="sticky left-0 top-0 bg-card z-40 p-4 text-sm font-semibold text-foreground">{t('landing.comparison.feature')}</th>
                                     {planIds.map((pid, i) => {
                                         const planName = planNameById[pid];
                                         // determine price line & CTA
                                         let priceLine = '';
                                         let periodLine = '';
                                         let ctaLabel = '';
                                         let ctaHref = '';
                                         let showPrice = true;
                                         if (pid === 'enterprise') {
                                             // Enterprise: no price, only CTA
                                             showPrice = false;
                                             ctaLabel = t('landing.pricing.contactSales');
                                             ctaHref = '/contact';
                                         } else {
                                             const p = getPrice(pid);
                                             priceLine = p.price;
                                             periodLine = p.period === '/year' ? '/year' : '/month';
                                             ctaLabel = t('landing.pricing.startFreeTrial');
                                             ctaHref = '/signup';
                                         }
                                         const isPopular = pid === 'professional';
                                         const displayLine = showPrice
                                             ? `${priceLine} ${periodLine}`
                                             : '';
                                         return (
                                            <th
                                                 key={pid}
                                                 className="p-4 text-center align-top sticky top-0 bg-card"
                                                 onMouseEnter={() => setHoveredPlanIdx(i)}
                                                 onMouseLeave={() => setHoveredPlanIdx(null)}
                                                 onTouchStart={() => setHoveredPlanIdx(i)}
                                             >
                                                 <div
                                                     className={cn(
                                                         'group rounded-md border border-border bg-card p-3 flex flex-col items-center gap-2 min-w-[160px] min-h-[130px] transition-colors',
                                                         hoveredPlanIdx === i && 'bg-gray-50 dark:bg-gray-800/20 border-gray-300'
                                                     )}
                                                 >
                                                     <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                                                         {planName}
                                                         {isPopular && (
                                                             <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500 text-white">{t('landing.comparison.mostPopular')}</span>
                                                         )}
                                                     </div>
                                                     {showPrice ? (
                                                         <div className="text-lg font-bold text-foreground leading-tight">
                                                             {displayLine}
                                                         </div>
                                                     ) : (
                                                         // Keep header heights consistent when price is hidden
                                                         <div className="text-lg font-bold leading-tight h-5" />
                                                     )}
                                                     <a
                                                         href={ctaHref}
                                                         className={cn(
                                                             'mt-1 inline-flex w-full items-center justify-center px-3 py-1.5 rounded-md text-xs font-semibold transition-colors border focus:outline-none focus:ring-2 focus:ring-ring',
                                                             pid === 'professional'
                                                                 ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                                                                 : 'bg-muted hover:bg-muted/80 text-foreground border-border'
                                                         )}
                                                         onFocus={() => setHoveredPlanIdx(i)}
                                                         onBlur={() => setHoveredPlanIdx(null)}
                                                     >
                                                         {ctaLabel}
                                                     </a>
                                                 </div>
                                             </th>
                                         );
                                     })}
                                 </tr>
                             </thead>
                             <tbody>
                                 {featureCategories.map((cat, ci) => (
                                     <React.Fragment key={ci}>
                                         <tr className="bg-muted/30">
                                             <td colSpan={1 + plansOrder.length} className="p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{cat.name}</td>
                                         </tr>
                                         {cat.rows.map((row, ri) => (
                                             <tr key={`${ci}-${ri}`} className={cn('border-b border-border', ri % 2 === 1 ? 'bg-card/50' : 'bg-card') }>
                                                 <td className="sticky left-0 bg-card z-10 p-4 text-sm text-foreground font-medium">{row.label}</td>
                                                 {row.values.map((v, i) => (
                                                     <td
                                                         key={`${ci}-${ri}-${i}`}
                                                         className={cn(
                                                             'p-4 text-center align-middle transition-colors border-l border-border',
                                                             hoveredPlanIdx === i && 'bg-gray-50 dark:bg-gray-800/20'
                                                         )}
                                                         onMouseEnter={() => setHoveredPlanIdx(i)}
                                                         onMouseLeave={() => setHoveredPlanIdx(null)}
                                                         onTouchStart={() => setHoveredPlanIdx(i)}
                                                     >
                                                         {renderCell(v)}
                                                     </td>
                                                 ))}
                                             </tr>
                                         ))}
                                     </React.Fragment>
                                 ))}
                             </tbody>
                             <tfoot>
                                 <tr className="bg-card border-t border-border">
                                     <td className="p-4 text-sm font-medium text-foreground">{t('landing.pricing.getStarted')}</td>
                                     {planIds.map((pid, i) => (
                                         <td key={pid} className="p-4 text-center border-l border-border">
                                             <a
                                                 href={pid === 'enterprise' ? '/contact' : '/signup'}
                                                 className={cn(
                                                     'inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-semibold border',
                                                     pid === 'professional' ? 'bg-blue-600 text-white border-blue-600' : 'bg-muted text-foreground border-border'
                                                 )}
                                             >
                                                 {pid === 'enterprise' ? t('landing.pricing.contactSales') : t('landing.pricing.startFreeTrial')}
                                             </a>
                                         </td>
                                     ))}
                                 </tr>
                             </tfoot>
                         </table>
                     </div>

                     <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2"><CheckIcon className="h-4 w-4 text-green-600" /> {t('landing.pricing.included')}</div>
                        <div className="flex items-center gap-2"><XMarkIcon className="h-4 w-4 text-rose-500" /> {t('landing.pricing.notIncluded')}</div>
                        <div className="flex items-center gap-2"><span className="inline-block h-4 w-4 rounded bg-muted border border-border"></span> {t('landing.pricing.numericValues')}</div>
                     </div>
                 </div>
             </section>

             {/* Trust Indicators with Testimonials */}
             <section className="py-16 sm:py-20 bg-card">
                 <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                     <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
                            {t('landing.testimonials.title')}
                        </h2>
                        
                        <blockquote className="max-w-3xl mx-auto text-xl italic text-foreground mb-2">
                            "{t('landing.testimonials.quote')}"
                        </blockquote>
                        <div className="flex items-center justify-center gap-1 mb-8" aria-label="5 star rating">
                            {Array.from({ length: 5 }).map((_, i) => <StarIcon key={i} className="h-5 w-5 text-yellow-500" />)}
                        </div>
                        <p className="text-sm text-muted-foreground font-medium mb-12">{t('landing.testimonials.author')}</p>
                     </div>

                     <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                         <div className="flex items-center gap-2">
                             <CheckCircleIcon className="h-5 w-5 text-green-500" />
                             <span>{t('landing.testimonials.trustPoints.euHosted')}</span>
                            </div>
                         <div className="flex items-center gap-2">
                             <CheckCircleIcon className="h-5 w-5 text-green-500" />
                             <span>{t('landing.testimonials.trustPoints.gdprCompliant')}</span>
                        </div>
                         <div className="flex items-center gap-2">
                             <CheckCircleIcon className="h-5 w-5 text-green-500" />
                             <span>{t('landing.testimonials.trustPoints.builtForAgencies')}</span>
                            </div>
                         <div className="flex items-center gap-2">
                             <CheckCircleIcon className="h-5 w-5 text-green-500" />
                             <span>{t('landing.testimonials.trustPoints.support247')}</span>
                        </div>
                    </div>
                </div>
            </section>

             {/* Integrations Section (inspired by HubSpot & HighLevel) - Hidden until implemented */}
             {false && (
                 <section className="py-16 sm:py-20 bg-muted">
                     <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                         <div className="text-center mb-12">
                             <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                                 Integrates with your favorite tools
                             </h2>
                             <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                                 Connect with the tools you already use to streamline your workflow
                             </p>
                         </div>
                         <div className="flex flex-wrap items-center justify-center gap-6 max-w-4xl mx-auto">
                             {integrations.map((integration, index) => (
                                 <div
                                     key={index}
                                     className="px-4 py-2 bg-card rounded-lg border border-border text-sm text-muted-foreground hover:border-blue-300 hover:text-foreground transition-colors"
                                 >
                                     {integration}
                                 </div>
                             ))}
                         </div>
                     </div>
                 </section>
             )}

            {/* Key Features Grid */}
            <section className="py-16 sm:py-20 lg:py-24 bg-muted">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                            {t('landing.keyFeatures.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('landing.keyFeatures.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        <FeatureCard
                            icon={UserGroupIcon}
                            title={t('landing.keyFeatures.clientManagement.title')}
                            description={t('landing.keyFeatures.clientManagement.description')}
                        />
                        <FeatureCard
                            icon={BoltIcon}
                            title={t('landing.keyFeatures.workflowAutomation.title')}
                            description={t('landing.keyFeatures.workflowAutomation.description')}
                        />
                        <FeatureCard
                            icon={SparklesIcon}
                            title={t('landing.keyFeatures.aiCopilot.title')}
                            description={t('landing.keyFeatures.aiCopilot.description')}
                        />
                        <FeatureCard
                            icon={ChartBarIcon}
                            title={t('landing.keyFeatures.advancedAnalytics.title')}
                            description={t('landing.keyFeatures.advancedAnalytics.description')}
                        />
                        <FeatureCard
                            icon={EnvelopeOpenIcon}
                            title={t('landing.keyFeatures.emailCampaigns.title')}
                            description={t('landing.keyFeatures.emailCampaigns.description')}
                        />
                        <FeatureCard
                            icon={GlobeAltIcon}
                            title={t('landing.keyFeatures.clientPortal.title')}
                            description={t('landing.keyFeatures.clientPortal.description')}
                        />
                        <FeatureCard
                            icon={ClipboardDocumentListIcon}
                            title={t('landing.keyFeatures.formsSurveys.title')}
                            description={t('landing.keyFeatures.formsSurveys.description')}
                        />
                        <FeatureCard
                            icon={ShieldCheckIcon}
                            title={t('landing.keyFeatures.whiteLabel.title')}
                            description={t('landing.keyFeatures.whiteLabel.description')}
                        />
                        <FeatureCard
                            icon={UserGroupIcon}
                            title={t('landing.keyFeatures.collaborationHub.title')}
                            description={t('landing.keyFeatures.collaborationHub.description')}
                        />
                    </div>
                </div>
            </section>

             {/* FAQ Section */}
             <section id="faq" className="py-16 sm:py-20 lg:py-24 bg-muted">
                 <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
                     <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                            {t('landing.faq.title')}
                        </h2>
                        </div>

                     <div className="space-y-6">
                         <FAQItem
                             question={t('landing.faq.freeTrial.question')}
                             answer={t('landing.faq.freeTrial.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.features.question')}
                             answer={t('landing.faq.features.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.nonRelocation.question')}
                             answer={t('landing.faq.nonRelocation.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.security.question')}
                             answer={t('landing.faq.security.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.integrations.question')}
                             answer={t('landing.faq.integrations.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.upgrade.question')}
                             answer={t('landing.faq.upgrade.answer')}
                         />
                         <FAQItem
                             question={t('landing.faq.languages.question')}
                             answer={t('landing.faq.languages.answer')}
                         />
                        </div>
                </div>
            </section>

             {/* Final CTA */}
             <section className="py-20 sm:py-24 lg:py-28 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
                 <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                     <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                         {t('landing.finalCta.title')}
                     </h2>
                     <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                         {t('landing.finalCta.subtitle')}
                     </p>
                     <a
                         href="/signup"
                         className="inline-flex items-center justify-center px-8 py-4 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2"
                     >
                        {t('landing.finalCta.button')}
                         <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </a>
                     <p className="text-sm text-blue-200 mt-4">
                         {t('landing.finalCta.note')}
                     </p>
                </div>
            </section>

             {/* Sticky CTA */}
            {showStickyCTA && (
                 <>
                     <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-2xl sm:hidden z-50">
                         <a
                             href="/signup"
                             className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                         >
                        {t('landing.hero.startTrial')}
                    </a>
                </div>
                     <div className="fixed bottom-6 right-6 hidden lg:block z-50">
                         <a
                             href="/signup"
                             className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
                         >
                        {t('landing.hero.startTrial')}
                             <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </a>
                </div>
                 </>
             )}

             {/* Footer */}
             <footer className="py-12 bg-card border-t border-border">
                 <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                     <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                         <div className="text-center md:text-left">
                             <img
                                 src="/LiveLoveSpain.png"
                                 alt="LiveLoveSpain"
                                 className="h-8 mx-auto md:mx-0 mb-4 opacity-80"
                             />
                             <p className="text-sm text-muted-foreground">
                                 &copy; {new Date().getFullYear()} LiveLoveSpain. All rights reserved.
                             </p>
                         </div>
                         <div className="flex flex-col md:flex-row items-center gap-4">
                             <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                                <a href="/privacy" className="text-muted-foreground hover:text-foreground underline">
                                     Privacy Policy
                                 </a>
                                 <span className="text-muted-foreground">•</span>
                                <a href="/terms" className="text-muted-foreground hover:text-foreground underline">
                                     Terms of Service
                                 </a>
                                 <span className="text-muted-foreground">•</span>
                                <a href="/dpa" className="text-muted-foreground hover:text-foreground underline">
                                    Data Processing Agreement
                                </a>
                                <span className="text-muted-foreground">•</span>
                                <a href="/cookies" className="text-muted-foreground hover:text-foreground underline">
                                    Cookies
                                </a>
                                <span className="text-muted-foreground">•</span>
                                 <a href="/contact" className="text-muted-foreground hover:text-foreground underline">
                                     Contact
                                 </a>
                             </div>
                         </div>
                     </div>
                 </div>
            </footer>

            {/* Cookie Consent Banner */}
            <CookieConsent />
        </div>
    );
};

 // Feature Card Component
 const FeatureCard = ({ icon: Icon, title, description }) => (
     <div className="p-6 rounded-xl bg-muted/50 border border-border hover:border-blue-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
         <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
             <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
         </div>
         <h3 className="text-lg font-semibold text-foreground mb-2">
             {title}
         </h3>
         <p className="text-sm text-muted-foreground">
             {description}
         </p>
     </div>
 );

 // FAQ Item Component - SEO: Using H3 for questions (H2 section → H3 questions)
 const FAQItem = ({ question, answer }) => {
     const [isOpen, setIsOpen] = useState(false);

     return (
         <div className="bg-card rounded-xl border border-border overflow-hidden">
             <button
                 onClick={() => setIsOpen(!isOpen)}
                 className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors"
                 aria-expanded={isOpen}
                 aria-controls={`faq-answer-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
             >
                 <h3 className="font-semibold text-foreground pr-4 text-left">{question}</h3>
                 {isOpen ? (
                     <XMarkIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                 ) : (
                     <ArrowRightIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 rotate-90" aria-hidden="true" />
                 )}
             </button>
             {isOpen && (
                 <div id={`faq-answer-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`} className="px-6 pb-6">
                     <p className="text-muted-foreground">{answer}</p>
                 </div>
             )}
        </div>
    );
};

export default LandingPage;
